/**
 * Status-token normalization and half-month granularity.
 *
 * Pure helper shared by the api's preview endpoint (to display the
 * interpreted state) and the worker (to decide what to write), so the state
 * shown in preview always matches what gets written. No DB, R2, or Redis —
 * only the raw cell value and the resolved profile's `statusDictionary`/
 * `polarity` are needed.
 *
 * Traceability: availability-import-pipeline / Requirement: Status-token
 * normalization and half-month granularity.
 */
import type { AvailabilityProfile } from '../../schemas/availability-profile.js';
import { pad } from './header-match.js';

export const CANONICAL_FREE_TOKENS = [
  'dostępny',
  'dostępna',
  'dostępne',
  'dostepny',
  'dostepna',
  'dostepne',
  'wolne',
  'wolna',
  'wolny',
] as const;
export const CANONICAL_BUSY_TOKENS = [
  'zajęta',
  'zajęty',
  'zajęte',
  'zajeta',
  'zajety',
  'zajete',
  'zaj',
  'sprzedany',
  'sprzedana',
  'sprzedane',
  'niedostępny',
  'niedostępna',
  'niedostępne',
  'niedostepny',
  'niedostepna',
  'niedostepne',
  'opcja',
  'rezerwacja',
  // Real supplier occupancy markers (Outdoor3miasto_pomorska.xlsx,
  // CU-86canaqmw): 'rez' (skrót od rezerwacji) and the bare 'x' cell —
  // previously unmapped, which silently skipped every occupied slot and
  // imported the file as free-only.
  'rez',
  'x',
] as const;

/**
 * Default `statusDictionary` built from the canonical free/busy vocabulary.
 * Resolvers reuse this instead of maintaining their own independent copy.
 */
export const DEFAULT_STATUS_DICTIONARY: Record<string, 'FREE' | 'UNAVAILABLE'> = {
  ...Object.fromEntries(CANONICAL_FREE_TOKENS.map((token) => [token, 'FREE' as const])),
  ...Object.fromEntries(CANONICAL_BUSY_TOKENS.map((token) => [token, 'UNAVAILABLE' as const])),
};

export type NormalizedStatusResult =
  | { state: 'FREE' | 'UNAVAILABLE' }
  | { unmapped: true; rawToken: string };

/**
 * Whether an already-normalized token reads as a canonical BUSY token: an
 * exact vocabulary hit, or a multi-word token whose FIRST word exactly
 * equals a canonical busy token ('rez zr', 'zaj do odwołania' — suppliers
 * routinely qualify the busy marker with free-text detail). Shared by
 * `normalizeStatusToken` below and by the resolvers' polarity inference, so
 * a status column holding only compound busy tokens can't be misclassified
 * as `list_of_free` (requirements-sweep finding on CU-86canaqmw).
 */
export function isBusyToken(normalized: string): boolean {
  if ((CANONICAL_BUSY_TOKENS as readonly string[]).includes(normalized)) return true;
  const firstWord = normalized.split(' ')[0]!;
  return (
    normalized.includes(' ') && (CANONICAL_BUSY_TOKENS as readonly string[]).includes(firstWord)
  );
}

/**
 * Trims, lowercases, and collapses whitespace for case/whitespace-insensitive
 * comparisons against the canonical status vocabulary. Exported for reuse by
 * the generic resolver's period-label-column heuristic, which needs the same
 * normalization to check a candidate column's distinct values against this
 * vocabulary.
 */
export function normalizeToken(raw: string): string {
  return raw.trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * Normalizes one row's raw status cell value to `FREE`/`UNAVAILABLE`,
 * case/whitespace-insensitively, or reports it as an unmapped token rather
 * than silently guessing.
 */
export function normalizeStatusToken(
  rawValue: string,
  profile: Pick<AvailabilityProfile, 'statusDictionary' | 'polarity'>,
): NormalizedStatusResult {
  const normalized = normalizeToken(rawValue);

  if (normalized === '') {
    return { state: 'FREE' };
  }

  const dictionaryHit = profile.statusDictionary[normalized];
  if (dictionaryHit) {
    return { state: dictionaryHit };
  }

  if ((CANONICAL_FREE_TOKENS as readonly string[]).includes(normalized)) {
    return { state: 'FREE' };
  }

  // Exact busy hit, or a multi-word token whose FIRST word is a canonical
  // busy token — a busy cell with a free-text qualifier ('rez zr',
  // 'rez uber zr', 'zaj do odwołania' — Outdoor3miasto_pomorska.xlsx,
  // CU-86canaqmw). The first-word rule is deliberately busy-only: a
  // qualified FREE phrase ('wolne od lipca') may carry period semantics, so
  // it stays unmapped rather than being flattened to whole-period FREE.
  if (isBusyToken(normalized)) {
    return { state: 'UNAVAILABLE' };
  }

  if (profile.polarity === 'list_of_busy') {
    // Any other non-blank value in a busy-listing file signals occupancy
    // (e.g. a client/reservation-holder name in an OMI-style export).
    return { state: 'UNAVAILABLE' };
  }

  return { unmapped: true, rawToken: rawValue };
}

/**
 * Computes the ISO date range for one half of a calendar month: 1st-15th for
 * the first half, 16th-end-of-month for the second half. End-of-month is
 * computed per calendar month (28/29/30/31 days) — never a naive "16-31".
 */
export function computeHalfMonthRange(
  year: number,
  month: number,
  half: 1 | 2,
): { start: string; end: string } {
  if (half === 1) {
    return { start: `${year}-${pad(month)}-01`, end: `${year}-${pad(month)}-15` };
  }
  const lastDay = new Date(year, month, 0).getDate();
  return { start: `${year}-${pad(month)}-16`, end: `${year}-${pad(month)}-${pad(lastDay)}` };
}

/**
 * Combines half-month period computation with status-token normalization
 * for a single half-month column's cell value. A half_month period column
 * SHALL produce exactly one interpreted period covering the half declared
 * by the column (not the full month).
 */
export function interpretHalfMonthCell(
  rawValue: string,
  year: number,
  month: number,
  half: 1 | 2,
  profile: Pick<AvailabilityProfile, 'statusDictionary' | 'polarity'>,
): { period: { start: string; end: string } } & NormalizedStatusResult {
  const period = computeHalfMonthRange(year, month, half);
  const normalized = normalizeStatusToken(rawValue, profile);
  return { period, ...normalized };
}
